import type { RouteLocation } from 'vue-router'
import { parseQuery } from 'vue-router'

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => !!localStorage.getItem('isAuthenticated')

/**
 * Whether a route is one of the public, unauthenticated pages (the onboarding portal,
 * the SSDI patient form, password reset, 404...).
 *
 * These are reachable by people who have no session at all, so the session machinery must
 * never navigate away from them: a stale `isAuthenticated` flag, an idle timer, a 401 from
 * some unrelated background call, or a logout in another tab would otherwise yank a visitor
 * off the page mid-task and strand them on /login (or the interstitial, after re-login).
 *
 * This gates only the client's *reaction* to auth state, never whether credentials are sent.
 * The session cookie must keep flowing on these routes — VH-2347 has the server read it to
 * let a Vector admin open an expired onboarding portal.
 */
export function isPublicRoute(route?: { meta?: Record<string, unknown> } | null): boolean {
  return String(route?.meta?.resource ?? '').toLowerCase() === 'public'
}

/**
 * Whether a route names a hospital the signed-in user isn't attached to.
 *
 * Vector admins can open any hospital. When the user's hospital list couldn't be loaded
 * this says no, and the API's own 403s still stand.
 */
export function isOtherHospital(providerUuid: unknown, memberUuids: string[], isVectorAdmin: boolean): boolean {
  if (isVectorAdmin || typeof providerUuid !== 'string' || providerUuid === '' || memberUuids.length === 0)
    return false

  return !memberUuids.includes(providerUuid)
}

/**
 * Whether a value is a path inside this app: one leading `/`, not `//host` or `/\host`,
 * which a browser treats as another site. Check this before following a `to` anywhere.
 */
export function isAppPath(value: unknown): value is string {
  // eslint-disable-next-line no-control-regex -- rejecting control characters is the point
  if (typeof value !== 'string' || !/^\/(?![/\\])/.test(value) || /[\u0000-\u001F\u007F]/.test(value))
    return false

  // Browsers strip tab/CR/LF inside URLs, so `/<TAB>/host` can normalise to `//host`; confirm it stays on our origin.
  // A fixed base (not window.location) keeps this usable where `window` is absent or stubbed.
  const base = 'http://app.invalid'

  try {
    return new URL(value, base).origin === base
  } catch {
    return false
  }
}

/**
 * Where `/` sends a visitor: the path in `?to=` if there is one, otherwise the dashboard
 * interstitial. 2FA login finishes by navigating to `/?to=<where the user was going>`.
 *
 * The target is split into path, query and hash explicitly. A `{ path }` object would drop
 * the target's own query (e.g. a product-update link's `utm_campaign`), and a plain string
 * without a `?` would inherit `/`'s query, carrying `to=` along. Only a path inside the app
 * is followed, and never `/` itself, which would redirect to itself.
 */
export function rootRedirect(to: RouteLocation) {
  const target = to.query.to
  if (isAppPath(target)) {
    const url = new URL(target, 'http://app.invalid')

    if (url.pathname !== '/')
      return { path: url.pathname, query: parseQuery(url.search), hash: url.hash }
  }

  const query = { ...to.query }

  delete query.to

  return { name: 'dashboard-interstitial', query }
}
